import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import { Table } from '@components/Common';
import { formatLocalDateTime } from '@helpers/formatting';
import { useAdminFilters } from '@hooks/useAdminFilters';
import AdminFiltersControlled, { filterItemsByDateTime } from './AdminFiltersControlled';
import AdminNavigation from './AdminNavigation';

// TanStack Query hooks
import {
  useFlowMetersHydrogenPeroxideQuery,
  FlowMeterHydrogenPeroxide
} from '@api/queries/flowMeters';

const FlowMetersHydrogenPeroxidePage: React.FC = () => {
  const { t } = useTranslation();

  // URL-based filters
  const { filters, dateFilter, setDateFilter } = useAdminFilters();

  // TanStack Query for data fetching
  const { data: items = [], isLoading, refetch } = useFlowMetersHydrogenPeroxideQuery(filters.startDate);

  const filteredItems = useMemo(() => filterItemsByDateTime(items, x => x.receivedAt, dateFilter), [items, dateFilter]);

  const columns: Column[] = [
    {
      Header: t('admin.received_at'),
      id: 'receivedAt',
      accessor: 'receivedAt',
      Cell: ({ value }) => formatLocalDateTime(value, true),
      show: true,
      maxWidth: 170,
      filterable: false
    },
    {
      Header: t('admin.end_date'),
      id: 'endDate',
      accessor: (item: FlowMeterHydrogenPeroxide) => {
        return formatLocalDateTime(item.endDate, true);
      },
      show: true,
      maxWidth: 170,
      filterable: false
    },
    {
      Header: t('entry.tank'),
      id: 'tankName',
      maxWidth: 150,
      accessor: 'tankName'
    },
    {
      Header: t('admin.register'),
      id: 'register',
      accessor: 'register',
      show: true,
      maxWidth: 120
    },
    {
      Header: t('admin.waybill_number'),
      id: 'waybillNumber',
      accessor: 'waybillNumber',
      show: true,
      maxWidth: 150
    },
    {
      Header: () => (
        <span>
          {t('admin.water')} <sup>kg</sup>
        </span>
      ),
      id: 'waterWeight',
      maxWidth: 120,
      filterable: true,
      accessor: 'waterWeight',
      filterMethod: (filter, row) => typeof row[filter.id] === 'number' && row[filter.id] === parseInt(filter.value),
      Cell: ({ value }) => (value ? value.toLocaleString('fi') : '')
    },
    {
      Header: () => (
        <span>
          H<sub>2</sub>O<sub>2</sub> <sup>kg</sup>
        </span>
      ),
      id: 'hydrogenPeroxideWeight',
      maxWidth: 120,
      filterable: true,
      accessor: 'hydrogenPeroxideWeight',
      filterMethod: (filter, row) => typeof row[filter.id] === 'number' && row[filter.id] === parseInt(filter.value),
      Cell: ({ value }) => (value ? value.toLocaleString('fi') : '')
    },
    {
      Header: () => (
        <span>
          {t('admin.total')} <sup>kg</sup>
        </span>
      ),
      id: 'amount',
      maxWidth: 120,
      filterable: true,
      accessor: 'totalWeight',
      filterMethod: (filter, row) => typeof row[filter.id] === 'number' && row[filter.id] === parseInt(filter.value),
      Cell: ({ value }) => (value ? value.toLocaleString('fi') : '')
    },
    {
      Header: () => (
        <span>
          {t('admin.concentration')} <sup>%</sup>
        </span>
      ),
      id: 'concentrationPercent',
      accessor: 'concentrationPercent',
      Cell: ({ value }) => value && value.toLocaleString('fi', { maximumFractionDigits: 10 }),
      filterMethod: (filter, row) =>
        typeof row[filter.id] === 'number' && Number(row[filter.id]) === parseFloat(filter.value.replace(',', '.')),
      maxWidth: 140
    },
    {
      Header: t('admin.connected'),
      id: 'connected',
      accessor: (item: FlowMeterHydrogenPeroxide) => !!item.bookkeepingEntryId,
      show: true,
      width: 90,
      Cell: ({ value }) => (value ? <strong>{t('filter_active')}</strong> : <span>{t('filter_inactive')}</span>),
      filterMethod: (filter, row) => (filter.value !== '' ? row[filter.id] === (filter.value === 'true') : true),
      Filter: ({ filter, onChange }) => (
        <select
          onChange={event => onChange(event.target.value)}
          style={{ width: '100%' }}
          value={filter ? filter.value : ''}
        >
          <option value="" />
          <option value="true">{t('filter_active')}</option>
          <option value="false">{t('filter_inactive')}</option>
        </select>
      )
    },
    {
      Header: 'Json',
      id: 'json',
      accessor: 'originalJsonData',
      show: true
    }
  ];

  return (
    <>
      <AdminNavigation isEnabled={true} isLoading={isLoading} fetch={() => refetch()} />
      <AdminFiltersControlled value={dateFilter} onChange={setDateFilter} />
      <Table
        data={filteredItems}
        columns={columns}
        filterable={true}
        defaultSortColumn="receivedAt"
        defaultSortDesc={true}
        defaultPageSize={20}
        noDataText={t('entry.no_rows_bookkeeping_reporting')}
      />
    </>
  );
};

export default FlowMetersHydrogenPeroxidePage;
